import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../../layout/mainLayout";
import { TransportOrderInvoicingState, TransportOrderListItemDto, TransportOrderListQueryParameters } from "../../../models/transport/transportOrder";
import { RootState, useAppSelector } from "../../../framework/customStore";
import { InvoicingTransportOrderList } from "./InvoicingTransportOrderList";
import { TransportOrderInvoiceSelectedOrders } from "./TransportOrderInvoiceSelectedOrders";
import { TransportOrderFilters } from "./TransportOrderFilters";
import { getTransportOrders } from "../../../services/transportOrderService";
import dayjs from "dayjs";
import { LoadingIndicator } from "../../framework/loadingIndicatorNew";
import Box from "@mui/material/Box";

export const DEFAULT_ORDERS_TIMERANGE = 14;

export const InvoicingTransportOrdersMain = () => {
    // Filters
    const [showOnlyInvoiceable, setShowOnlyInvoiceable] = useState(true);
    const [filterCustomer, setFilterCustomer] = useState<string>("");
    const [timeRange, setTimeRange] = useState<[Date, Date]>([
        dayjs().startOf("date").subtract(DEFAULT_ORDERS_TIMERANGE, "days").toDate(),
        dayjs().endOf("date").toDate(),
    ]);
    const [keyword, setKeyword] = useState<string>("");
    const [loading, setLoading] = useState(false);

    // Pagination
    const [page, setPage] = useState(0); // 0-based
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    // Data
    const [orderItems, setOrderItems] = useState<TransportOrderListItemDto[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<TransportOrderListItemDto[]>([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");

    // Redux state
    const state = useAppSelector((state: RootState) => state.transportOrders);

    // Fetch orders
    const fetchOrders = useCallback(async() => {
        setLoading(true);
        try {
            const qsp: TransportOrderListQueryParameters = {
                ...(filterCustomer && { billingCustomerId: filterCustomer }),
                ...(keyword && { keyword }),
                page: page + 1, // API expects 1-based
                pageSize: rowsPerPage,
                ...(showOnlyInvoiceable
                    ? {
                        ...(timeRange[0] && { actualDeliveryStart: timeRange[0].toISOString() }),
                        ...(timeRange[1] && { actualDeliveryEnd: timeRange[1].toISOString() }),
                        invoicingState: TransportOrderInvoicingState.Invoiceable,
                    }
                    : {
                        ...(timeRange[0] && { timeFrameStartDate: timeRange[0].toISOString() }),
                        ...(timeRange[1] && { timeFrameEndDate: timeRange[1].toISOString() }),
                    }),
            };
            const res = await getTransportOrders(qsp);
            if (res?.data) {
                setOrderItems(res.data.items);
                setTotalCount(res.data.totalCount || 0);
            }
        } finally {
            setLoading(false);
        }
    }, [showOnlyInvoiceable, filterCustomer, timeRange, keyword, page, rowsPerPage]);

    useEffect(() => {
        void fetchOrders();
    }, [fetchOrders]);

    // Selected customer logic
    useEffect(() => {
        if (selectedOrders.length > 0) {
            setSelectedCustomerId(selectedOrders[0]?.billingCustomerId);
        } else {
            setSelectedCustomerId("");
        }
    }, [selectedOrders]);

    useEffect(() => {
        if (
            selectedOrders.length > 0 &&
            selectedCustomerId !== state.billingCustomerId &&
            state.billingCustomerId
        ) {
            setSelectedOrders([]);
        }
    }, [state.billingCustomerId, selectedCustomerId, selectedOrders.length]);

    // Toggle invoiceable filter
    const handleToggleState = () => {
        setShowOnlyInvoiceable((prev) => !prev);
        setPage(0); // Reset to first page on filter change
    };

    // Handlers for filters
    const handleFilterCustomerChange = (val: string) => {
        setFilterCustomer(val);
        setPage(0);
    };
    const handleTimeRangeChange = (val: [Date, Date]) => {
        setTimeRange(val);
        setPage(0);
    };
    const handleKeywordChange = (val: string) => {
        setKeyword(val);
        setPage(0);
    };

    return (
        <MainLayout
            className="colored-sidebar"
            sideComponent={
                <TransportOrderInvoiceSelectedOrders selectedOrders={selectedOrders} />
            }
            topComponent={
                <TransportOrderFilters
                    filterCustomer={filterCustomer}
                    setFilterCustomer={handleFilterCustomerChange}
                    timeRange={timeRange}
                    setTimeRange={handleTimeRangeChange}
                    setKeyword={handleKeywordChange}
                />
            }
        >
            <div style={{ position: "relative", minHeight: 56 + rowsPerPage * 53 + 56 }}>
                {loading && (
                <Box sx={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: "rgba(255,255,255,0.7)", zIndex: 10
                }}
                >
                    <LoadingIndicator />
                </Box>
                )}
                {!loading && (
                <InvoicingTransportOrderList
                    orderItems={orderItems}
                    handleToggleState={handleToggleState}
                    showOnlyInvoiceable={showOnlyInvoiceable}
                    selectedOrders={selectedOrders}
                    setSelectedOrders={setSelectedOrders}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    totalCount={totalCount}
                />
                )}
            </div>
        </MainLayout>
    );
};
